import { useState } from "react";
import { API } from "@/lib/api";
import { JackstoneLogo } from "@/components/Logo";
import { ShieldCheck, Lock } from "lucide-react";

/**
 * Tier-1 company gate. The user must authenticate with a company Google account
 * before the internal login page is shown.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 * The redirect_uri is built from window.location.origin at click time.
 */
export default function GoogleGate() {
  const [busy, setBusy] = useState(false);

  const startGoogle = () => {
    setBusy(true);
    const redirectUri = window.location.origin + "/auth/google";
    window.location.href = `${API}/auth/google/start?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#f5f1ec]">
      {/* Left brand panel */}
      <div className="relative md:w-1/2 bg-[#33424f] text-white p-8 md:p-14 flex flex-col justify-between overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #fff 0, #fff 14px, transparent 14px, transparent 28px)",
          }}
        />
        <div className="relative">
          <JackstoneLogo className="h-8 w-auto" variant="light" />
          <div className="mt-3 text-[10px] subtitle tracking-[0.3em] uppercase text-[#ccb795]">
            Pressure Test Registry
          </div>
        </div>
        <div className="relative">
          <div className="text-[10px] subtitle uppercase tracking-[0.3em] text-[#ccb795] mb-3">
            Restricted system
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-none">
            COMPANY ACCESS ONLY
          </h1>
          <p className="font-body text-sm text-slate-300 mt-4 max-w-md">
            This is an internal Jackstone tool. Sign in with your company Google
            account to continue. Access is limited to verified
            <span className="text-white"> @jackstone.co.uk</span> accounts.
          </p>
        </div>
        <div className="relative text-[11px] text-slate-400 subtitle uppercase tracking-[0.2em]">
          PED 2014/68/EU · Secured
        </div>
      </div>

      {/* Right auth panel */}
      <div className="md:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="text-[10px] subtitle uppercase tracking-[0.3em] text-[#39678f] mb-2">
            Step 1 of 2 · Company sign-in
          </div>
          <h2 className="font-display text-3xl text-[#33424f] tracking-tight leading-none mb-6">
            VERIFY YOUR COMPANY ACCOUNT
          </h2>

          <button
            type="button"
            data-testid="continue-with-google-btn"
            onClick={startGoogle}
            disabled={busy}
            className="w-full h-12 inline-flex items-center justify-center gap-3 border border-[#33424f]/20 bg-white hover:bg-[#faf8f4] text-[#33424f] subtitle uppercase tracking-wider text-xs disabled:opacity-60"
          >
            <GoogleIcon />
            {busy ? "Redirecting…" : "Continue with Google"}
          </button>

          <div className="mt-6 space-y-3 text-xs text-[#50493b] font-body">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-[#39678f] mt-0.5 shrink-0" />
              Only verified company Google Workspace accounts are accepted.
            </div>
            <div className="flex items-start gap-2">
              <Lock className="w-4 h-4 text-[#39678f] mt-0.5 shrink-0" />
              After this step, log in with your normal app credentials.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
